import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { makeStyles } from "@material-ui/core";

import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { CampaignStatus, JobStatus } from "src/graphql/generated/globalTypes";
import { loggedInInfo_loggedInInfo_tenant } from "src/graphql/generated/loggedInInfo";
import {
  fieldIsAlwaysRequired,
  questionsContainField
} from "src/util/calleeFields";

import ConfirmChangeContactSourceDialog from "./ConfirmChangeContactSourceDialog";
import ContactSources, { ContactSource, ImportStatus } from "./ContactSources";
import ContinueButton from "./ContinueButton";
import CsvImport, { hasValidCsvImport } from "./CsvImport";
import VanImport, { hasValidVanImport } from "./VanImport";

const useStyles = makeStyles(theme => ({
  contactSourceWrapper: {
    margin: theme.spacing(3)
  }
}));

export default function Contacts({
  campaign,
  goToNextSection,
  refreshCampaign
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
  refreshCampaign: () => void;
}): JSX.Element {
  const classes = useStyles();

  const [contactSource, setContactSource] = useState<ContactSource>(
    campaign.vanIntegration && !hasValidCsvImport(campaign) ? "VAN" : "CSV"
  );
  const [newSourceToConfirm, setNewSourceToConfirm] =
    useState<ContactSource | null>(null);
  const [overwriting, setOverwriting] = useState(false);

  let initialImportStatus: ImportStatus;
  if (!campaign.contactImportJob) {
    initialImportStatus = "PREPARING";
  } else if (
    campaign.contactImportJob.status == JobStatus.QUEUED ||
    campaign.contactImportJob.status === JobStatus.RUNNING
  ) {
    initialImportStatus = "IN_PROGRESS";
  } else {
    initialImportStatus = "DONE";
  }

  const [importStatus, setImportStatus] =
    useState<ImportStatus>(initialImportStatus);
  useEffect(() => {
    if (importStatus === "DONE") {
      setOverwriting(false);
    }
  }, [importStatus]);

  const handleSelectContactSource = (source: ContactSource) => {
    switch (importStatus) {
      case "PREPARING":
        setContactSource(source);
        break;
      case "IN_PROGRESS":
        break;
      case "DONE":
        setNewSourceToConfirm(source);
    }
  };

  function renderComponentForSource() {
    switch (contactSource) {
      case "CSV":
        return (
          <CsvImport
            campaign={campaign}
            onChangeImportStatus={setImportStatus}
            overwriting={overwriting}
            onClearContacts={() => setOverwriting(true)}
          />
        );
      case "VAN":
        return (
          <VanImport
            campaign={campaign}
            onChangeImportStatus={setImportStatus}
            overwriting={overwriting}
            onClearContacts={() => setOverwriting(true)}
            refreshCampaign={refreshCampaign}
          />
        );
    }
  }

  return (
    <>
      <RouteLeavingGuard when={overwriting} />
      <h2>Add Contacts</h2>
      <ContactSources
        campaign={campaign}
        onSelectSource={handleSelectContactSource}
        selectedSource={contactSource}
        disabled={
          campaign.status !== CampaignStatus.DRAFT ||
          importStatus === "IN_PROGRESS"
        }
      />
      <div className={classes.contactSourceWrapper}>
        {renderComponentForSource()}
      </div>
      <ContinueButton handleClick={goToNextSection} />
      <ConfirmChangeContactSourceDialog
        open={!!newSourceToConfirm}
        onClose={() => setNewSourceToConfirm(null)}
        onConfirm={() => {
          setContactSource(newSourceToConfirm!);
          setNewSourceToConfirm(null);
          setOverwriting(true);
        }}
      />
    </>
  );
}

export function hasContacts(campaign: getCampaign_campaign): boolean {
  return (
    campaign.contactImportJob?.status === JobStatus.DONE &&
    (hasValidVanImport(campaign) || hasValidCsvImport(campaign))
  );
}

export function validate({
  campaign,
  subaccountLink
}: {
  tenant: loggedInInfo_loggedInInfo_tenant;
  campaign: getCampaign_campaign;
  subaccountLink: (relativePath: string) => string;
}): JSX.Element | null {
  if (!hasContacts(campaign)) {
    return (
      <span>
        You must{" "}
        <Link to={subaccountLink(`/campaigns/${campaign.id}/contacts`)}>
          upload contacts
        </Link>{" "}
        before starting your campaign.
      </span>
    );
  }

  const { nContacts } = JSON.parse(campaign.contactImportJob!.result || "{}");
  if (!nContacts) {
    return (
      <span>
        You must{" "}
        <Link to={subaccountLink(`/campaigns/${campaign.id}/contacts`)}>
          upload at least one contact
        </Link>{" "}
        before starting your campaign.
      </span>
    );
  }

  const calleeFieldsNeedingMapping = campaign.calleeFields.filter(
    field =>
      (questionsContainField(campaign.questions, field) ||
        fieldIsAlwaysRequired(field)) &&
      !field.dataSourceFieldName
  );
  if (calleeFieldsNeedingMapping.length) {
    return (
      <span>
        You must{" "}
        <Link to={subaccountLink(`/campaigns/${campaign.id}/contacts`)}>
          map required contact fields
        </Link>{" "}
        ({calleeFieldsNeedingMapping.map(field => field.name).join(", ")})
        before starting your campaign.
      </span>
    );
  }

  return null;
}
